import { localDay, type NotationDrill, type Puzzle } from '@familyhub/shared';
import type { SchaakState } from './actions';
import type { GameRecord, MistakeRecord } from './db';

/** What the Schaak screen is showing (docs/SCHAAK.md). */
export type Mode =
  | { kind: 'train'; themes: string[] | null }
  | { kind: 'woodpecker' }
  | { kind: 'mistakes'; gameId?: string }
  | { kind: 'notation' }
  | { kind: 'notation-lesson' }
  | { kind: 'notation-drill'; drill: NotationDrill }
  | { kind: 'summary'; summary: SessionSummary };

/** What a session did, for the summary shown when it stops. */
export interface SessionSummary {
  attempts: { label: string; solved: boolean }[];
  ratingFrom: number;
  ratingTo: number;
}

/** A position from his own game, as a puzzle: the opponent's move, then the best reply. */
export function mistakePuzzle(m: MistakeRecord): Puzzle {
  return { id: m.id, fen: m.fen, moves: [m.opp_move, m.best], rating: 0, theme: m.kind, accept: m.accept };
}

/** Mistakes not practised yet, newest game first. */
export function freshMistakes(state: SchaakState): MistakeRecord[] {
  const order = new Map(state.games.map((g, i) => [g.id, i]));
  return state.mistakes
    .filter((m) => !state.cards[m.id])
    .sort((a, b) => (order.get(a.game_id) ?? 0) - (order.get(b.game_id) ?? 0) || a.ply - b.ply);
}

export const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });

/** The game on its own site, at the move where it went wrong (Lichess counts plies from 1). */
export function gameLink(g: GameRecord, ply?: number): string {
  return g.source === 'lichess' && ply != null ? `${g.url}#${ply + 1}` : g.url;
}

export const RESULT: Record<string, { short: string; label: string }> = {
  win: { short: 'W', label: 'gewonnen' },
  loss: { short: 'V', label: 'verloren' },
  draw: { short: '½', label: 'remise' },
};

/** Attempts today, from the day log (counts a puzzle seen twice twice). */
export function todayCount(state: SchaakState, now: Date): number {
  return state.days.find((d) => d.d === localDay(now))?.n ?? 0;
}

/** "1 fout" / "3 fouten" — Dutch plurals the lazy way, for the words used here. */
export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
